import type { TemplateResult } from '@design.estate/dees-element';
import type { IHarnessToolCall, THarnessContentBlock } from './interfaces.js';
import { harnessIsRecord, harnessSummaryLine } from './harness.helpers.js';

/**
 * Render helpers handed to tool body renderers. Implemented by
 * dees-harness-tool-card (which owns the templates); typed here so custom
 * descriptors can be authored against a stable surface.
 */
export interface IHarnessToolRenderHelpers {
  /** highlighted code block (dees-dataview-codebox) with optional filename */
  codeBlock(code: string, language?: string, filename?: string): TemplateResult;
  /** before/after diff (dees-dataview-codebox diff mode) */
  diffBlock(before: string, after: string, options?: { language?: string; filename?: string; view?: 'inline' | 'split' }): TemplateResult;
  /** plain monospace block with truncation + expand */
  monoBlock(text: string, options?: { maxChars?: number; tone?: 'default' | 'error' }): TemplateResult;
  /** two-column key/value summary */
  keyValues(pairs: Array<[string, string]>): TemplateResult;
  /** MCP content blocks (dees-harness-content-blocks) */
  contentBlocks(blocks: THarnessContentBlock[]): TemplateResult;
  /** constrained inline image from base64 */
  image(dataBase64: string, mimeType: string, alt?: string): TemplateResult;
}

export interface IHarnessToolDescriptor {
  /** stable kind id, e.g. 'terminal', 'file-read', 'mcp', 'unknown' */
  kind: string;
  /** exact raw-name matches (fast path); lowercased on registration */
  names?: string[];
  /** fallback matcher when no name matched */
  match?: (call: IHarnessToolCall) => boolean;
  label: string | ((call: IHarnessToolCall) => string);
  /** lucide icon spec, e.g. 'lucide:Terminal' */
  icon: string;
  subtitle?: (call: IHarnessToolCall) => string;
  /**
   * Custom body renderer. Built-in kinds leave this unset — the tool card
   * ships body templates for them — so descriptors stay DOM-free unless a
   * consumer wants a bespoke body.
   */
  renderBody?: (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers) => TemplateResult;
  defaultExpanded?: boolean | ((call: IHarnessToolCall) => boolean);
  /** styles the card with a destructive accent regardless of status */
  destructive?: boolean;
}

/** Splits smartagent's MCP namespacing convention `server__tool`. */
export const parseMcpName = (
  rawName: string,
): { serverName: string; toolName: string } | undefined => {
  const separatorIndex = rawName.indexOf('__');
  if (separatorIndex <= 0 || separatorIndex >= rawName.length - 2) return undefined;
  return {
    serverName: rawName.slice(0, separatorIndex),
    toolName: rawName.slice(separatorIndex + 2),
  };
};

const inputField = (call: IHarnessToolCall, key: string): string => {
  return harnessIsRecord(call.input) && typeof call.input[key] === 'string'
    ? (call.input[key] as string)
    : '';
};

const inputAny = (call: IHarnessToolCall, key: string): unknown => {
  return harnessIsRecord(call.input) ? call.input[key] : undefined;
};

const filePathOf = (call: IHarnessToolCall): string =>
  inputField(call, 'path') || inputField(call, 'filePath') || inputField(call, 'file');

const defaultDescriptors: IHarnessToolDescriptor[] = [
  {
    kind: 'terminal',
    names: ['run_command', 'bash', 'shell', 'exec'],
    label: 'Shell',
    icon: 'lucide:Terminal',
    subtitle: (call) => {
      const command = inputField(call, 'command');
      const cwd = inputField(call, 'cwd') || inputField(call, 'workdir');
      return command ? `${command}${cwd ? ` — in ${cwd}` : ''}` : harnessSummaryLine(call.input);
    },
    defaultExpanded: true,
  },
  {
    kind: 'file-read',
    names: ['read_file', 'read'],
    label: 'Read',
    icon: 'lucide:FileText',
    subtitle: (call) => {
      const path = filePathOf(call);
      const startLine = inputAny(call, 'startLine');
      const endLine = inputAny(call, 'endLine');
      const range = startLine || endLine ? `:${startLine ?? ''}-${endLine ?? ''}` : '';
      return path ? `${path}${range}` : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'file-write',
    names: ['write_file', 'write', 'edit', 'edit_file', 'apply_patch', 'patch'],
    label: 'Write',
    icon: 'lucide:FilePen',
    subtitle: (call) => filePathOf(call) || harnessSummaryLine(call.input),
  },
  {
    kind: 'file-delete',
    names: ['delete_file', 'delete', 'remove_file'],
    label: 'Delete',
    icon: 'lucide:Trash2',
    subtitle: (call) => filePathOf(call) || harnessSummaryLine(call.input),
    destructive: true,
  },
  {
    kind: 'dir-list',
    names: ['list_directory', 'list', 'ls'],
    label: 'List',
    icon: 'lucide:FolderOpen',
    subtitle: (call) => {
      const path = filePathOf(call) || '.';
      return inputAny(call, 'recursive') ? `${path} (recursive)` : path;
    },
  },
  {
    kind: 'browser',
    names: ['browser'],
    label: 'Browser',
    icon: 'lucide:Globe',
    subtitle: (call) => {
      const action = inputField(call, 'action') || 'snapshot';
      const target = inputField(call, 'url') || inputField(call, 'selector');
      return `${action}${target ? ` ${target}` : ''}`;
    },
  },
  {
    kind: 'http',
    names: ['http_get', 'http_post', 'webfetch', 'fetch'],
    label: (call) => (call.name === 'http_post' ? 'HTTP POST' : call.name === 'http_get' ? 'HTTP GET' : 'Fetch'),
    icon: 'lucide:ArrowDownUp',
    subtitle: (call) => inputField(call, 'url') || harnessSummaryLine(call.input),
  },
  {
    kind: 'json',
    names: ['json_validate', 'json_transform'],
    label: (call) => (call.name === 'json_transform' ? 'JSON Transform' : 'JSON Validate'),
    icon: 'lucide:Braces',
    subtitle: (call) => {
      const source = inputField(call, 'jsonString');
      return source ? (source.length > 60 ? `${source.slice(0, 60)}…` : source) : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'search',
    names: ['grep', 'glob', 'websearch', 'search'],
    label: 'Search',
    icon: 'lucide:Search',
    subtitle: (call) =>
      inputField(call, 'pattern') || inputField(call, 'query') || filePathOf(call) || harnessSummaryLine(call.input),
  },
  {
    kind: 'subtask',
    names: ['task', 'subagent'],
    label: 'Subagent',
    icon: 'lucide:Bot',
    subtitle: (call) => inputField(call, 'description') || inputField(call, 'prompt') || harnessSummaryLine(call.input),
  },
  {
    kind: 'question',
    names: ['askuserquestion', 'ask_user_question', 'ask_question', 'ask_user', 'question'],
    label: 'Question',
    icon: 'lucide:MessageCircleQuestionMark',
    subtitle: (call) => inputField(call, 'question') || inputField(call, 'prompt') || harnessSummaryLine(call.input),
    defaultExpanded: true,
  },
  {
    kind: 'todo',
    names: ['todowrite', 'todo_write', 'todoread'],
    label: 'Todos',
    icon: 'lucide:ListTodo',
    subtitle: (call) => {
      const todos = inputAny(call, 'todos');
      return Array.isArray(todos) ? `${todos.length} todos` : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'mcp',
    label: (call) => call.mcp?.annotations?.title || call.mcp?.toolName || parseMcpName(call.name)?.toolName || call.name,
    icon: 'lucide:Plug',
    match: (call) => Boolean(call.mcp) || Boolean(parseMcpName(call.name)),
    subtitle: (call) => harnessSummaryLine(call.input),
  },
  {
    kind: 'unknown',
    label: (call) => call.name || 'Tool',
    icon: 'lucide:Wrench',
    subtitle: (call) => harnessSummaryLine(call.input),
  },
];

/**
 * Maps raw tool calls to render descriptors. A pre-seeded singleton covers
 * the built-in and MCP conventions; apps register custom tools once at boot
 * (`DeesHarnessToolRegistry.default.register(...)`) or hand an isolated
 * `fork()` to individual elements via their `.toolRegistry` property.
 */
export class DeesHarnessToolRegistry {
  public static readonly default: DeesHarnessToolRegistry = new DeesHarnessToolRegistry(defaultDescriptors);

  private descriptors: IHarnessToolDescriptor[] = [];
  private byName = new Map<string, IHarnessToolDescriptor>();
  private byKind = new Map<string, IHarnessToolDescriptor>();

  constructor(seedDescriptors: IHarnessToolDescriptor[] = []) {
    for (const descriptor of seedDescriptors) {
      this.register(descriptor);
    }
  }

  /** Registers (or replaces, by kind) a descriptor. Later wins on names. */
  public register(descriptor: IHarnessToolDescriptor): this {
    const existing = this.byKind.get(descriptor.kind);
    if (existing) {
      this.descriptors = this.descriptors.filter((candidate) => candidate !== existing);
      for (const [name, mapped] of this.byName) {
        if (mapped === existing) this.byName.delete(name);
      }
    }
    this.byKind.set(descriptor.kind, descriptor);
    this.descriptors.push(descriptor);
    for (const name of descriptor.names ?? []) {
      this.byName.set(name.toLowerCase(), descriptor);
    }
    return this;
  }

  public unregister(kind: string): this {
    const existing = this.byKind.get(kind);
    if (!existing) return this;
    this.byKind.delete(kind);
    this.descriptors = this.descriptors.filter((candidate) => candidate !== existing);
    for (const [name, mapped] of this.byName) {
      if (mapped === existing) this.byName.delete(name);
    }
    return this;
  }

  public descriptorForKind(kind: string): IHarnessToolDescriptor | undefined {
    return this.byKind.get(kind);
  }

  /** Never returns undefined — falls back to the 'unknown' descriptor. */
  public resolve(call: IHarnessToolCall): IHarnessToolDescriptor {
    if (call.kind) {
      const explicit = this.byKind.get(call.kind);
      if (explicit) return explicit;
    }
    const named = this.byName.get(String(call.name || '').toLowerCase());
    if (named) return named;
    for (const descriptor of this.descriptors) {
      if (descriptor.match?.(call)) return descriptor;
    }
    return this.byKind.get('unknown') ?? defaultDescriptors[defaultDescriptors.length - 1];
  }

  public resolveKind(name: string): string {
    return this.resolve({ id: '', name, status: 'completed' }).kind;
  }

  /** Isolated copy for demos, tests, or per-pane toolsets. */
  public fork(): DeesHarnessToolRegistry {
    return new DeesHarnessToolRegistry(this.descriptors.map((descriptor) => ({ ...descriptor })));
  }
}
